/**
 * FinTech Model Configuration
 * COMPLETE extraction from /server/fintech/fintech-api.js
 */

import type { ModelConfig } from '@/types/models';
import { standardResultTabs } from '@/config/resultTabs';

export const fintechConfig: ModelConfig = {
  modelType: 'fintech',
  apiEndpoint: '/api/fintech',
  displayName: 'FinTech',
  resultTabs: standardResultTabs,
  description: 'Financial technology platform with TPV, take rate, and payment processing.',
  icon: '💳',
  color: '#22c55e',

  assumptionGroups: [
    {
      id: 'user-details',
      title: '👤 Your Details',
      fields: [
        { id: 'userEmail', label: 'Your Email', type: 'email', placeholder: 'name@company.com' },
      ],
    },
    {
      id: 'core-revenue',
      title: '💰 Core Revenue Drivers',
      fields: [
        { id: 'monthlyTPV', label: 'Monthly TPV ($)', type: 'number', defaultValue: 5000000 },
        { id: 'takeRate', label: 'Take Rate (%)', type: 'number', defaultValue: 2.9 },
        { id: 'fixedFeePerTxn', label: 'Fixed Fee/Txn ($)', type: 'number', defaultValue: 0.30 },
        { id: 'activeUsers', label: 'Active Users', type: 'number', defaultValue: 5000 },
        { id: 'transactionsPerUser', label: 'Transactions/User/Month', type: 'number', defaultValue: 15 },
        { id: 'churnRate', label: 'Monthly Churn (%)', type: 'number', defaultValue: 2 },
      ],
    },
    {
      id: 'network-participants',
      title: '👥 Network Participants',
      fields: [
        { id: 'activeBuyers', label: 'Active Buyers', type: 'number', defaultValue: 2850 },
        { id: 'activeSellers', label: 'Active Sellers', type: 'number', defaultValue: 385 },
        { id: 'buyerToSellerRatio', label: 'Buyer:Seller Ratio', type: 'number', defaultValue: 7.4 },
        { id: 'buyerTransactionsPerMonth', label: 'Buyer Txns/Month', type: 'number', defaultValue: 2.4 },
        { id: 'sellerTransactionsPerMonth', label: 'Seller Txns/Month', type: 'number', defaultValue: 17.5 },
        { id: 'buyerChurnRate', label: 'Buyer Churn (%)', type: 'number', defaultValue: 15 },
        { id: 'sellerChurnRate', label: 'Seller Churn (%)', type: 'number', defaultValue: 8 },
      ],
    },
    {
      id: 'growth',
      title: '📈 Growth & Network Effects',
      fields: [
        { id: 'growthRateY2', label: 'Y2 Growth (%)', type: 'number', defaultValue: 26 },
        { id: 'growthRateY3', label: 'Y3 Growth (%)', type: 'number', defaultValue: 18 },
        { id: 'growthRateY4', label: 'Y4 Growth (%)', type: 'number', defaultValue: 12 },
        { id: 'growthRateY5', label: 'Y5 Growth (%)', type: 'number', defaultValue: 8 },
      ],
    },
    {
      id: 'revenue-streams',
      title: '💵 Revenue Streams',
      fields: [
        { id: 'paymentProcessingPct', label: 'Payment Processing (%)', type: 'number', defaultValue: 2.1 },
        { id: 'listingFeesPerSeller', label: 'Listing Fees/Seller ($)', type: 'number', defaultValue: 0 },
        { id: 'advertisingRevenuePct', label: 'Advertising Revenue (%)', type: 'number', defaultValue: 0 },
        { id: 'premiumSellersPct', label: 'Premium Sellers (%)', type: 'number', defaultValue: 0 },
        { id: 'monthlyPremiumFee', label: 'Premium Fee/Month ($)', type: 'number', defaultValue: 0 },
        { id: 'additionalServicesPct', label: 'Additional Services (%)', type: 'number', defaultValue: 0 },
      ],
    },
    {
      id: 'variable-costs',
      title: '📊 Variable Costs (COGS)',
      fields: [
        { id: 'paymentProcessingCostPct', label: 'Payment Processing Cost (%)', type: 'number', defaultValue: 2.1 },
        { id: 'fraudPreventionPct', label: 'Fraud Prevention (%)', type: 'number', defaultValue: 0.8 },
        { id: 'supportCostPerTransaction', label: 'Support Cost/Txn ($)', type: 'number', defaultValue: 0.50 },
        { id: 'serviceCostPerTransaction', label: 'Service Cost/Txn ($)', type: 'number', defaultValue: 0.25 },
      ],
    },
    {
      id: 'acquisition',
      title: '🎯 Acquisition Costs',
      fields: [
        { id: 'buyerCAC', label: 'Buyer CAC ($)', type: 'number', defaultValue: 45 },
        { id: 'sellerCAC', label: 'Seller CAC ($)', type: 'number', defaultValue: 285 },
      ],
    },
    {
      id: 'operating-expenses',
      title: '💼 Operating Expenses',
      fields: [
        { id: 'teamSize', label: 'Team Size', type: 'number', defaultValue: 22 },
        { id: 'avgSalary', label: 'Avg Salary ($)', type: 'number', defaultValue: 92000 },
        { id: 'annualWageIncrease', label: 'Annual Wage Increase (%)', type: 'number', defaultValue: 4 },
        { id: 'salesMarketingPct', label: 'Sales & Marketing (%)', type: 'number', defaultValue: 28 },
        { id: 'techCosts', label: 'Monthly Tech Costs (K)', type: 'number', defaultValue: 18 },
        { id: 'techCostPerTransaction', label: 'Tech Cost/Txn ($)', type: 'number', defaultValue: 0.05 },
        { id: 'gaPct', label: 'G&A (%)', type: 'number', defaultValue: 12 },
      ],
    },
    {
      id: 'fixed-costs',
      title: '🏢 Fixed Costs',
      fields: [
        { id: 'annualProfessionalServices', label: 'Annual Prof. Services ($)', type: 'number', defaultValue: 150000 },
        { id: 'monthlyOfficeRent', label: 'Monthly Office Rent ($)', type: 'number', defaultValue: 25000 },
        { id: 'otherOpExPct', label: 'Other OpEx (%)', type: 'number', defaultValue: 2 },
      ],
    },
    {
      id: 'fixed-assets',
      title: '🏗️ Fixed Assets',
      fields: [
        { id: 'initialEquipmentValue', label: 'Equipment Value ($)', type: 'number', defaultValue: 120000 },
        { id: 'initialSoftwareValue', label: 'Software Value ($)', type: 'number', defaultValue: 250000 },
        { id: 'equipmentDepreciationRate', label: 'Equipment Depreciation (%)', type: 'number', defaultValue: 25 },
        { id: 'softwareAmortizationRate', label: 'Software Amortization (%)', type: 'number', defaultValue: 33 },
        { id: 'maintenanceCapexPct', label: 'Maintenance CapEx (%)', type: 'number', defaultValue: 1 },
        { id: 'softwareCapexPct', label: 'Software CapEx (%)', type: 'number', defaultValue: 3 },
      ],
    },
    {
      id: 'debt-interest',
      title: '🏦 Debt & Interest',
      fields: [
        { id: 'initialDebtAmount', label: 'Initial Debt ($)', type: 'number', defaultValue: 600000 },
        { id: 'annualInterestRate', label: 'Interest Rate (%)', type: 'number', defaultValue: 8 },
        { id: 'debtRepaymentYears', label: 'Repayment Years', type: 'number', defaultValue: 7 },
      ],
    },
    {
      id: 'working-capital',
      title: '💰 Working Capital',
      fields: [
        { id: 'daysInAccountsReceivable', label: 'Days in AR', type: 'number', defaultValue: 15 },
        { id: 'daysInAccountsPayable', label: 'Days in AP', type: 'number', defaultValue: 30 },
        { id: 'prepaidExpensesPct', label: 'Prepaid Expenses (%)', type: 'number', defaultValue: 3 },
        { id: 'accruedExpensesPct', label: 'Accrued Expenses (%)', type: 'number', defaultValue: 5 },
        { id: 'minimumCashBalance', label: 'Minimum Cash ($)', type: 'number', defaultValue: 300000 },
      ],
    },
    {
      id: 'capital-raising',
      title: '🚀 Capital Raising',
      fields: [
        { id: 'initialCapitalRaise', label: 'Initial Capital ($)', type: 'number', defaultValue: 2000000 },
        { id: 'year2CapitalRaise', label: 'Year 2 Capital ($)', type: 'number', defaultValue: 5000000 },
        { id: 'year3CapitalRaise', label: 'Year 3 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'year4CapitalRaise', label: 'Year 4 Capital ($)', type: 'number', defaultValue: 10000000 },
        { id: 'year5CapitalRaise', label: 'Year 5 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'automaticCapitalRaise', label: 'Automatic Capital Raise', type: 'checkbox', defaultValue: true, helpText: 'Automatically raise capital when cash goes negative' },
      ],
    },
    {
      id: 'financial-tax',
      title: '🏛️ Financial & Tax',
      fields: [
        { id: 'corporateTaxRate', label: 'Corporate Tax Rate (%)', type: 'number', defaultValue: 25 },
      ],
    },
  ],

};

export const fintechDefaultAssumptions = {
  monthlyTPV: 5000000,
  takeRate: 2.9,
  fixedFeePerTxn: 0.30,
  activeUsers: 5000,
  transactionsPerUser: 15,
  churnRate: 2,
  activeBuyers: 2850,
  activeSellers: 385,
  buyerToSellerRatio: 7.4,
  buyerTransactionsPerMonth: 2.4,
  sellerTransactionsPerMonth: 17.5,
  buyerChurnRate: 15,
  sellerChurnRate: 8,
  growthRateY2: 26,
  growthRateY3: 18,
  growthRateY4: 12,
  growthRateY5: 8,
  paymentProcessingPct: 2.1,
  listingFeesPerSeller: 0,
  advertisingRevenuePct: 0,
  premiumSellersPct: 0,
  monthlyPremiumFee: 0,
  additionalServicesPct: 0,
  paymentProcessingCostPct: 2.1,
  fraudPreventionPct: 0.8,
  supportCostPerTransaction: 0.50,
  serviceCostPerTransaction: 0.25,
  buyerCAC: 45,
  sellerCAC: 285,
  teamSize: 22,
  avgSalary: 92000,
  annualWageIncrease: 4,
  salesMarketingPct: 28,
  techCosts: 18,
  techCostPerTransaction: 0.05,
  gaPct: 12,
  annualProfessionalServices: 150000,
  monthlyOfficeRent: 25000,
  otherOpExPct: 2,
  corporateTaxRate: 25,
  initialEquipmentValue: 120000,
  initialSoftwareValue: 250000,
  equipmentDepreciationRate: 25,
  softwareAmortizationRate: 33,
  maintenanceCapexPct: 1,
  softwareCapexPct: 3,
  initialDebtAmount: 600000,
  annualInterestRate: 8,
  debtRepaymentYears: 7,
  daysInAccountsReceivable: 15,
  daysInAccountsPayable: 30,
  prepaidExpensesPct: 3,
  accruedExpensesPct: 5,
  minimumCashBalance: 300000,
  initialCapitalRaise: 2000000,
  year2CapitalRaise: 5000000,
  year3CapitalRaise: 0,
  year4CapitalRaise: 10000000,
  year5CapitalRaise: 0,
  automaticCapitalRaise: true,
  revenueMultiple: 5.5,
  ebitdaMultiple: 15,
  gmvMultiple: 0.5,
  discountRate: 0.15,
  terminalGrowth: 0.02,
};
